import { DatePipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY, Observable, filter, finalize, firstValueFrom, switchMap, take, takeUntil, throwIfEmpty } from 'rxjs';
import { AuthenticationService } from '../../authentication/authentication.service';
import { BillingMembershipDto, MceApiService, SharedBillingManagementSessionDto } from '../../core/mceapi.client.generated';
import { appOpt, getApiErrorMessage } from '../../core/mceapi.client.helpers';
import { DialogService } from '../../shared/dialogs/dialog.service';
import { MceDataTableComponent } from '../../shared/components/mce-data-table/mce-data-table.component';
import { isSome } from '../../shared/functions/functional';
import { hasRole, Role } from '../../shared/enums/role';
import { PageWithNavigationComponent } from '../../shared/page-templates/page-with-navigation/page-with-navigation.component';
import { ManagementOtpDialogComponent } from './management-otp-dialog/management-otp-dialog.component';

@Component({
  selector: 'mce-shared-billing-users',
  imports: [PageWithNavigationComponent, RouterLink, MatButtonModule, DatePipe, MceDataTableComponent, MatIconModule, MatMenuModule, MatProgressBarModule, MatToolbarModule],
  templateUrl: './shared-billing-users.component.html',
})
export class SharedBillingUsersComponent implements OnInit {
  private readonly auth = inject(AuthenticationService);
  private readonly api = inject(MceApiService);
  private readonly dialogService = inject(DialogService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly context = computed(() => {
    const roles = this.auth.currentUserRoles();
    return {
      uid: this.auth.user()?.uid,
      eligible: this.auth.currentUserMatchesSignedInAccount()
        && hasRole(roles, Role.ProTier) && !hasRole(roles, Role.SharedBillingMember),
    };
  });
  private readonly context$ = toObservable(this.context);
  private readonly managementSession = signal<SharedBillingManagementSessionDto | undefined>(undefined);
  private verifiedUid: string | undefined;
  protected readonly verified = computed(() => !!this.managementSession()?.sessionToken
    && this.context().eligible && this.context().uid === this.verifiedUid);

  protected readonly processing = signal(false);
  protected readonly operationError = signal('');
  protected readonly columns = ['email', 'statusLabel', 'joined', 'actions'];
  protected readonly headers = { email: 'Email', statusLabel: 'Status', joined: 'Joined', actions: '' };
  protected readonly membersResource = rxResource({
    params: () => {
      const sessionToken = this.managementSession()?.sessionToken;
      return this.verified() && sessionToken ? { uid: this.context().uid, sessionToken } : undefined;
    },
    stream: ({ params }) => this.api.v1SharedBillingMembersGet(params.sessionToken),
  });
  protected readonly members = computed(() => {
    if (this.membersResource.error()) return [];
    return (this.membersResource.value() ?? []).map(member => {
      const joined = appOpt(member.joinedTime);
      return {
        ...member,
        statusLabel: member.status === 'active' ? 'Accepted' : member.status === 'pending' ? 'Pending' : member.status ?? 'Unknown',
        joined: isSome(joined) ? joined.value.value : undefined,
      };
    });
  });
  protected readonly loadError = computed(() => {
    const error = this.membersResource.error();
    return error ? getApiErrorMessage(error) : '';
  });

  protected async addUser(): Promise<void> {
    await this.runAction(async () => {
      const model = await this.dialogService.userEmail();
      return model ? sessionToken => this.api.v1SharedBillingInvitationsPost(sessionToken, { email: model.email }) : undefined;
    });
  }

  protected async removeUser(member: BillingMembershipDto): Promise<void> {
    const uid = member.uid;
    if (!uid) return;
    await this.runAction(async () => {
      const pending = member.status === 'pending';
      const confirmed = await firstValueFrom(this.dialogService.confirm(
        pending ? 'Cancel invitation' : 'Remove user',
        pending
          ? 'Cancel the shared billing invitation for ' + member.email + '?'
          : 'Remove ' + member.email + ' from shared billing? Their usage will return to their own plan and its limits.',
        '280px',
      ));
      return confirmed ? sessionToken => this.api.v1SharedBillingMembersDelete(uid, sessionToken) : undefined;
    });
  }

  protected async resendInvitation(member: BillingMembershipDto): Promise<void> {
    const uid = member.uid;
    if (!uid || member.status !== 'pending') return;
    await this.runAction(async () => sessionToken => this.api.v1SharedBillingInvitationsResend(uid, sessionToken));
  }

  private async runAction(prepare: () => Promise<((sessionToken: string) => Observable<unknown>) | undefined>): Promise<void> {
    const sessionToken = this.managementSession()?.sessionToken;
    if (!sessionToken || !this.verified() || this.processing() || this.destroyRef.destroyed) return;
    const uid = this.context().uid;
    const sameAccount = () => !this.destroyRef.destroyed && this.verified() && this.context().uid === uid
      && this.managementSession()?.sessionToken === sessionToken;
    this.processing.set(true);
    this.operationError.set('');
    try {
      const operation = await prepare();
      if (!operation || !sameAccount()) return;
      await firstValueFrom(operation(sessionToken).pipe(
        takeUntil(this.context$.pipe(filter(context => context.uid !== uid || !context.eligible))),
        takeUntilDestroyed(this.destroyRef),
      ));
      if (sameAccount()) this.membersResource.reload();
    } catch (error) {
      if (sameAccount()) this.operationError.set(getApiErrorMessage(error));
    } finally {
      this.processing.set(false);
    }
  }
  ngOnInit(): void {
    const { uid, eligible } = this.context();
    if (!uid || !eligible) {
      this.returnToSettings();
      return;
    }

    const accountChanged$ = this.context$.pipe(filter(context => context.uid !== uid || !context.eligible));
    accountChanged$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.managementSession.set(undefined);
      this.returnToSettings();
    });

    let ref: MatDialogRef<ManagementOtpDialogComponent, string> | undefined;
    this.api.v1SharedBillingManagementOtp().pipe(
      take(1),
      throwIfEmpty(() => new Error('Could not send a one time password.')),
      switchMap(token => {
        if (!token.token) throw new Error('Could not send a one time password.');
        ref = this.dialog.open<ManagementOtpDialogComponent, void, string>(ManagementOtpDialogComponent, {
          width: '480px',
          maxWidth: '95vw',
          ariaLabel: 'Verify shared billing access',
          ariaDescribedBy: 'management-otp-description',
          disableClose: true,
        });
        return ref.afterClosed().pipe(
          take(1),
          switchMap(otp => {
            if (!otp) {
              this.returnToSettings();
              return EMPTY;
            }
            return this.api.v1SharedBillingManagementVerify({ otpToken: token.token, otp }).pipe(
              take(1),
              throwIfEmpty(() => new Error('Could not verify shared billing access.')),
            );
          }),
        );
      }),
      takeUntil(accountChanged$),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        if (ref?.getState() === MatDialogState.OPEN) ref.close();
      }),
    ).subscribe({
      next: session => {
        if (!session.sessionToken) {
          this.verificationFailed(new Error('Could not verify shared billing access.'));
          return;
        }
        this.verifiedUid = uid;
        this.managementSession.set(session);
      },
      error: error => this.verificationFailed(error),
    });
  }

  private verificationFailed(error: unknown): void {
    this.snackBar.open(getApiErrorMessage(error), 'Dismiss', { duration: 8000 });
    this.returnToSettings();
  }

  private returnToSettings(): void {
    void this.router.navigate(['/settings'], { replaceUrl: true });
  }
}
